import React, { useEffect, useState, useCallback, FC } from 'react';
import { useHistory } from 'react-router-dom';

import getAuthUser from 'helpers/getAuthUser';
import { useFeaturedFlagEnabled } from 'helpers/posthogHooks';
import { ANALYTICS_EVENTS } from 'constants/analyticsEvents';
import { getGradientImage } from 'components/Sparks/constants';
import { useNavigationRef } from 'components/Navigation/helpers/hooks';
import { COLORS } from 'styles/constants';
import { useGetSparkDetail, useGetSparkDetailCards } from 'graphQL/sparks/hooks';
import { ROUTE_DISCOVER } from 'routes';
import useToast from 'helpers/useToast';
import { EFeaturedFlags } from 'types/EFeatureFlags';
import LoaderContent from 'components/UI/LoaderContent';
import PageBackground from 'components/Profile/PageBackground';
import useTheme from 'helpers/useTheme';

import { useAnalytics } from 'contexts/AnalyticsContext';

import noiseTextureUrl from 'assets/images/noise.png';
import sparkIconLight from 'assets/videos/spark-icon-light.webm';
import Avatar from 'components/UI/Avatar/Avatar';
import { getUserInfo, generateName } from 'components/Profile/ProfileTop/helpers';
import SparkSelectionModal from 'components/Profile/SparksList/SparkSelectionModal';
import SparkResponseCard from './SparkResponseCard';
import {
  StyledPageContainer,
  StyledHeaderSection,
  StyledCoverCard,
  StyledCoverCardVideo,
  StyledSparkDescription,
  StyledBodySection,
  StyledSection,
  StyledSectionHeader,
  StyledCardsContainer,
  StyledLoadingContainer,
  StyledOwnerSection,
  StyledOwnerRow,
  StyledOwnerInfo,
  StyledOwnerName,
  StyledOwnerBio,
  StyledOwnerThought,
  StyledFloatingActionButton,
  StyledThoughtEmptyState,
  StyledHowItWorksButton,
  StyledEmptyStateContainer,
} from './styled';

// Helper to determine if a hex color is dark based on relative luminance
const isColorDark = (hexColor?: string): boolean => {
  if (!hexColor) return false;
  const hex = hexColor.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  // Calculate relative luminance using sRGB formula
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance < 0.5;
};

const SparkDetail: FC = () => {
  const { track } = useAnalytics();
  const isInteractSparks = useFeaturedFlagEnabled(EFeaturedFlags.InteractSpark);
  const isViewSparks = useFeaturedFlagEnabled(EFeaturedFlags.ViewSparks);
  const isDark = useTheme();

  const ref = useNavigationRef(COLORS.white[100]);

  const { spark, isQueryCalled, error } = useGetSparkDetail();
  const { title, description } = spark ?? {};

  // Use description if present, otherwise fall back to title
  const displayThought = description || title;

  const { predefinedGradientName, gradientStart } = spark?.cardStyle?.bgColor ?? {};

  // Use user's selected cover media if available, otherwise fall back to gradient
  // Type assertion needed until GraphQL types are regenerated
  const typedSpark = spark as typeof spark & {
    userCoverSelection?: {
      mediaUrl?: string;
      mediaType?: string;
      gradientStart?: string;
      gradientEnd?: string;
    };
  };
  const coverMediaUrl = typedSpark?.userCoverSelection?.mediaUrl;
  const isAnimatedCover = typedSpark?.userCoverSelection?.mediaType === 'ANIMATION';
  const bgImage = isAnimatedCover ? undefined : coverMediaUrl || getGradientImage(predefinedGradientName ?? '');

  // Use the stored gradient colors from cover selection (extracted at creation time),
  // or fall back to spark's gradient colors
  const extractedStartColor = typedSpark?.userCoverSelection?.gradientStart;
  const extractedEndColor = typedSpark?.userCoverSelection?.gradientEnd;
  const pageGradientStart = extractedStartColor ?? gradientStart ?? (isDark ? COLORS.black[100] : COLORS.white[100]);
  const pageBaseColor = extractedEndColor ?? (isDark ? COLORS.black[100] : COLORS.white[100]);

  const { items, loading, totalCount } = useGetSparkDetailCards(spark);

  // Check if current user is the owner of this spark
  const { userId } = getAuthUser();
  const isOwner = spark?.user?.id === userId;

  const { replace } = useHistory();
  const { setToast } = useToast();

  // Modal state for spark creation
  const [isSparkModalOpen, setIsSparkModalOpen] = useState(false);

  // Modal state for "How it works" intro
  const [isHowItWorksOpen, setIsHowItWorksOpen] = useState(false);

  useEffect(() => {
    if (isQueryCalled && (!spark || error)) {
      replace(ROUTE_DISCOVER);
      setToast({ isToastOpen: true, toastItemName: 'Content not found' });
    }
  }, [isQueryCalled, error, spark, replace, setToast]);

  useEffect(() => {
    track(ANALYTICS_EVENTS.QotDOpen);
    track(ANALYTICS_EVENTS.SparkAction);
  }, [track]);

  // Get the categorySlug from the spark (with type assertion until types are regenerated)
  const categorySlug = (spark as typeof spark & { categorySlug?: string })?.categorySlug;

  const handleCreateOwn = () => {
    if (isInteractSparks) {
      setIsSparkModalOpen(true);
    }
  };

  const handleCloseSparkModal = () => {
    setIsSparkModalOpen(false);
  };

  // Floating action button visibility based on scroll
  const [isFabVisible, setIsFabVisible] = useState(false);

  const handleScroll = useCallback(() => {
    // Show FAB after scrolling past the header section (roughly 500px)
    const scrollThreshold = 500;
    setIsFabVisible(window.scrollY > scrollThreshold);
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  // All responses displayed in a single list
  const responses = items ?? [];

  if (!isViewSparks) return null;

  return (
    <PageBackground startColor={pageGradientStart} baseColor={pageBaseColor} overlayHeightPx={600} useRadialGradient>
      <StyledPageContainer ref={ref}>
        {/* Header Section */}
        <StyledHeaderSection>
          <StyledCoverCard bgImage={bgImage}>
            {isAnimatedCover && coverMediaUrl && (
              <StyledCoverCardVideo src={coverMediaUrl} autoPlay loop muted playsInline />
            )}
          </StyledCoverCard>
          {spark?.user &&
            (() => {
              const { firstName, lastName, bio } = getUserInfo(spark.user);
              const displayName = generateName(spark.user.username, firstName, lastName);
              const isDarkBg = isColorDark(pageBaseColor);
              return (
                <StyledOwnerSection bgColor={extractedEndColor} bgImage={noiseTextureUrl}>
                  <StyledOwnerRow>
                    <Avatar user={spark.user} dimension={44} />
                    <StyledOwnerInfo>
                      <StyledOwnerName isDark={isDarkBg}>{displayName}</StyledOwnerName>
                      {bio && <StyledOwnerBio isDark={isDarkBg}>{bio}</StyledOwnerBio>}
                    </StyledOwnerInfo>
                  </StyledOwnerRow>
                  {displayThought ? (
                    <StyledOwnerThought isDark={isDarkBg}>&ldquo;{displayThought}&rdquo;</StyledOwnerThought>
                  ) : (
                    isOwner && (
                      <StyledThoughtEmptyState isDark={isDarkBg}>
                        Tap to add your thoughts for this Spark
                      </StyledThoughtEmptyState>
                    )
                  )}
                </StyledOwnerSection>
              );
            })()}

          {/* Non-owner view - show "create your own" prompt */}
          {/* {!isOwner && isInteractSparks && (
            <StyledButtonSection bgColor={extractedEndColor} bgImage={noiseTextureUrl}>
              <StyledSectionIntro isDark={isColorDark(pageBaseColor)}>
                Everyone has a list like this.{'\n'}These are the ones that shaped yours.
              </StyledSectionIntro>
              <StyledCreateOwnButton onClick={handleCreateOwn}>Create your own</StyledCreateOwnButton>
            </StyledButtonSection>
          )} */}
        </StyledHeaderSection>

        {/* Body Section */}
        <StyledBodySection>
          {loading ? (
            <StyledLoadingContainer>
              <LoaderContent isCentered isDark={isDark} />
            </StyledLoadingContainer>
          ) : (
            <>
              {/* Spark Responses Section */}
              {responses.length > 0 && (
                <StyledSection gradientEndColor={pageBaseColor} isDark={isDark}>
                  <StyledCardsContainer>
                    {responses.map((card, index) => {
                      // First card uses gradientEndColor to determine if text should be light/dark
                      const cardIsDark = index === 0 ? isColorDark(pageBaseColor) : isDark;
                      return (
                        <SparkResponseCard
                          key={card.id}
                          card={card}
                          isDark={cardIsDark}
                          footerIsDarkMode={(index === 0 && isDark) || isDark}
                          sparkTitle={title ?? undefined}
                          isOwner={isOwner}
                        />
                      );
                    })}
                  </StyledCardsContainer>
                </StyledSection>
              )}

              {/* Empty State */}
              {totalCount === 0 && (
                <StyledSection gradientEndColor={pageBaseColor} isDark={isDark}>
                  {!isOwner && (
                    <>
                      <StyledSectionHeader>No responses yet</StyledSectionHeader>
                      <StyledCardsContainer>
                        <StyledSparkDescription style={{ color: COLORS.brown.light[100] }}>
                          Be the first to respond to this spark!
                        </StyledSparkDescription>
                      </StyledCardsContainer>
                    </>
                  )}
                  <StyledEmptyStateContainer>
                    <StyledHowItWorksButton onClick={() => setIsHowItWorksOpen(true)}>
                      How it works
                    </StyledHowItWorksButton>
                  </StyledEmptyStateContainer>
                </StyledSection>
              )}
            </>
          )}
        </StyledBodySection>

        {/* Floating Action Button - appears on scroll for non-owners */}
        {!isOwner && isInteractSparks && (
          <StyledFloatingActionButton isVisible={isFabVisible} onClick={handleCreateOwn}>
            <video src={sparkIconLight} autoPlay loop muted playsInline />
            Create your own
          </StyledFloatingActionButton>
        )}
      </StyledPageContainer>
      {/* Spark Creation Modal */}
      <SparkSelectionModal
        isOpen={isSparkModalOpen}
        onClose={handleCloseSparkModal}
        initialCategorySlug={categorySlug}
      />
      {/* How it works Modal (reuses SparkSelectionModal with intro) */}
      <SparkSelectionModal isOpen={isHowItWorksOpen} onClose={() => setIsHowItWorksOpen(false)} showIntro />
    </PageBackground>
  );
};

export default SparkDetail;
